import { getNavigationContext, useEmbedContext, } from '@thoughtspot/blink-context';
import { getNonce } from '@thoughtspot/codec-util';
import { FEATURE_FLAG_NAMES, isFeatureEnabled, } from '@thoughtspot/feature-service';
import { isSafariOptimizationEnabled } from '@thoughtspot/session-service';
import _ from 'lodash';
import React from 'react';
import { decodeEmbedParams, getEmbedQueryParams, isEmbeddedApp } from '.';
import { ACTION_ALIAS, ActionItemMap, ADDITIONAL_PENDO_KEY, APP_THEME_CSS_ELEMENT_ID, BREAKPOINT_LOWER_BOUND, BUFFER_HEIGHT, CUSTOM_CSS_ELEMENT_ID, CUSTOM_CSS_URL_ELEMENT_ID, DISABLE_WORKSHEET_CHANGE, FIRST_BREAKPOINT_BREAKPOINT_VARIABLE, FIRST_BREAKPOINT_DEFAULT_VALUE, HIDE_EUREKA_SUGGESTIONS, HIDE_SAGE_ANSWER_HEADER, HIDE_SEARCH_BAR_TITLE, IS_FULL_APP_EMBED, IS_LINKOVERRIDE_ENABLED, IS_LIVEBOARD_EMBED, IS_PINBOARD_VIZ_EMBED, IS_PRODUCT_TOUR, IS_SAGE_EMBED, IS_SEARCH_EMBED, IS_SEARCH_WORKSHEET_SELECTOR_HIDDEN, IS_SPOTTER_AGENT_EMBED, IS_SPOTTER_EMBED, MIN_DIFF, SECOND_BREAKPOINT_BREAKPOINT_VARIABLE, SECOND_BREAKPOINT_DEFAULT_VALUE, } from './constants';
import { Action, EventType, IncomingEventType, } from './types';
import { ActionSelect } from './types/prism';
const runtimeFilterParams = {};
const runtimeParameterParams = {};
let hostConfig;
let hiddenHomeLeftNavItems = [];
let embedCssParams = {};
let hiddenHomepageModules = [];
let reorderedHomepageModules = [];
const embedActionsMapForParamsMap = new Map();
const answerIdToVizGUIDMap = new Map();
let embedQueryPramsFromInitData = {};
let embedCssStyles = {};
let appThemeCssVariables = {};
const EXTERNAL_VARIABLE_PREFIX = '--ts-var-';
let hiddenListColumns = [];
let codeBasedCustomActions = [];
const setHostConfig = (_hostConfig) => {
    hostConfig = _hostConfig;
};
const getHostConfig = () => {
    return hostConfig;
};
const setHiddenHomeLeftNavItems = (_hiddenHomeLeftNavItems) => {
    hiddenHomeLeftNavItems = _hiddenHomeLeftNavItems;
};
const getHiddenHomeLeftNavItems = () => {
    return hiddenHomeLeftNavItems;
};
const setEmbedCssParams = (_embedCssParams) => {
    embedCssParams = _embedCssParams;
};
const getEmbedCssParams = (variable) => {
    return embedCssParams?.[variable];
};
const setHiddenHomepageModules = (_hiddenHomepageModules) => {
    hiddenHomepageModules = _hiddenHomepageModules;
};
const getHiddenHomepageModules = () => {
    return hiddenHomepageModules;
};
const setHiddenListColumns = (_hiddenListColumns) => {
    hiddenListColumns = _hiddenListColumns;
};
const getHiddenListColumns = () => {
    return hiddenListColumns;
};
const setReorderedHomepageModules = (_reorderedHomepageModules) => {
    reorderedHomepageModules = _reorderedHomepageModules;
};
const getReorderedHomepageModules = () => {
    return reorderedHomepageModules;
};
const setCustomActions = (_customActions) => {
    codeBasedCustomActions = _customActions;
};
const getCustomActions = () => {
    return codeBasedCustomActions;
};
const JsonStringtoArrayRequestHeaders = (urlHeaders) => {
    return Object.values(JSON.parse(urlHeaders));
};
const isMobileWebViewMessagingEnabled = () => {
    return isFeatureEnabled(FEATURE_FLAG_NAMES.ENABLE_MOBILE_WEBVIEW_MESSAGING);
};
const isMobileEmbedShell = () => {
    try {
        if (typeof window === 'undefined' || !window.location) {
            return false;
        }
        const params = new URLSearchParams(window.location.search || '');
        const hostAppUrl = params.get('hostAppUrl');
        if (hostAppUrl &&
            hostAppUrl.includes('mobile-embed-shell.vercel.app')) {
            return true;
        }
        if (typeof document !== 'undefined' &&
            document.referrer &&
            document.referrer.includes('mobile-embed-shell.vercel.app')) {
            return true;
        }
        return false;
    }
    catch (error) {
        return false;
    }
};
const urlAuthHeadersForCutomActions = (actionInfo) => {
    let authHeader = {};
    switch (actionInfo.authSelect) {
        case ActionSelect.Basic:
            authHeader = {
                Authorization: `Basic ${actionInfo.encodeUser}`,
            };
            break;
        case ActionSelect.Apikey:
            authHeader = { [actionInfo.apiKey]: actionInfo.apiValue };
            break;
        case ActionSelect.Bearer:
            authHeader = {
                Authorization: `Bearer ${actionInfo.authToken}`,
            };
            break;
        default:
            authHeader = {};
    }
    return authHeader;
};
const isSearchEmbed = () => {
    const queryParams = getEmbedQueryParams();
    return queryParams[IS_SEARCH_EMBED] || false;
};
const isSageEmbed = () => {
    const queryParams = getEmbedQueryParams();
    return queryParams[IS_SAGE_EMBED] || false;
};
const hideSageAnswerHeader = () => {
    const queryParams = getEmbedQueryParams();
    return queryParams[HIDE_SAGE_ANSWER_HEADER] || false;
};
const hideSearchBarTitle = () => {
    const queryParams = getEmbedQueryParams();
    return queryParams[HIDE_SEARCH_BAR_TITLE] || false;
};
const hideWorksheetSelector = () => {
    const queryParams = getEmbedQueryParams();
    return queryParams[IS_SEARCH_WORKSHEET_SELECTOR_HIDDEN] || false;
};
const disableWorksheetChange = () => {
    const queryParams = getEmbedQueryParams();
    return queryParams[DISABLE_WORKSHEET_CHANGE] || false;
};
const isProductTour = () => {
    const queryParams = getEmbedQueryParams();
    return queryParams[IS_PRODUCT_TOUR] || false;
};
const hideEurekaSuggestions = () => {
    const queryParams = getEmbedQueryParams();
    return queryParams[HIDE_EUREKA_SUGGESTIONS] || false;
};
const isSearchBarHidden = () => {
    const queryParams = getEmbedQueryParams();
    return queryParams[IS_SEARCH_EMBED] ?? false;
};
const isLiveboardEmbed = () => {
    const queryParams = getEmbedQueryParams();
    return queryParams[IS_LIVEBOARD_EMBED] || false;
};
const isSpotterAgentEmbed = () => {
    const queryParams = getEmbedQueryParams();
    return queryParams[IS_SPOTTER_AGENT_EMBED] || false;
};
const isSpotterEmbed = () => {
    const queryParams = getEmbedQueryParams();
    return queryParams[IS_SPOTTER_EMBED] || false;
};
const isFullAppEmbed = () => {
    const queryParams = getEmbedQueryParams();
    return queryParams[IS_FULL_APP_EMBED] || false;
};
const isLBHeaderHiddenInEmbed = (embedConfig) => {
    const { embedParams, isEmbedded } = embedConfig;
    if (!isEmbedded)
        return false;
    return embedParams?.hideLiveboardHeader || false;
};
const isOnBeforeGetVizDataInterceptEnabled = () => {
    const queryParams = getEmbedQueryParams();
    return queryParams?.isOnBeforeGetVizDataInterceptEnabled || false;
};
const isApiInterceptDisabled = () => {
    const queryParams = getEmbedQueryParams();
    return queryParams?.isApiInterceptDisabled || false;
};
const isFilterPanelHiddenInEmbed = (embedConfig) => {
    if (!embedConfig?.isEmbedded)
        return false;
    return embedConfig?.embedParams?.hideFilterPanel || false;
};
const shouldShowLBTitleInEmbed = (embedConfig) => {
    const { embedParams, isEmbedded } = embedConfig;
    if (!isEmbedded)
        return true;
    if (_.isUndefined(embedParams?.showLiveboardTitle)) {
        return !isLiveboardEmbed();
    }
    return embedParams?.showLiveboardTitle;
};
const shouldShowLiveboardVerifiedBadge = (embedConfig) => {
    const { embedParams, isEmbedded } = embedConfig;
    if (!isEmbedded)
        return true;
    return embedParams?.showLiveboardVerifiedBadge ?? true;
};
const shouldShowLiveboardReverifyBanner = (embedConfig) => {
    const { embedParams, isEmbedded } = embedConfig;
    if (!isEmbedded)
        return true;
    return embedParams?.showLiveboardReverifyBanner ?? true;
};
const shouldShowLBDescriptionInEmbed = (embedConfig) => {
    const { embedParams, isEmbedded } = embedConfig;
    if (!isEmbedded)
        return true;
    if (_.isUndefined(embedParams?.showLiveboardDescription)) {
        return !isLiveboardEmbed();
    }
    return embedParams?.showLiveboardDescription;
};
const shouldShowAnswerEditPanelInEmbed = (embedConfig) => {
    const { embedParams, isEmbedded } = embedConfig;
    if (!isEmbedded)
        return true;
    return embedParams?.showAnswerEditPanel ?? true;
};
const shouldShowMaskedFilterChipInEmbed = (embedConfig) => {
    const { embedParams, isEmbedded } = embedConfig;
    if (!isEmbedded)
        return true;
    return embedParams?.showMaskedFilterChip ?? false;
};
const linkOverrideEnabled = () => {
    const queryParams = getEmbedQueryParams();
    return queryParams[IS_LINKOVERRIDE_ENABLED] || false;
};
const isLinkOverridesV2Enabled = () => {
    const queryParams = getEmbedQueryParams();
    return !!queryParams?.enableLinkOverridesV2;
};
const isDisableRedirectionLinksInNewTabEnabled = () => {
    const queryParams = getEmbedQueryParams();
    return !!queryParams?.disableRedirectionLinksInNewTab;
};
const isVizEmbed = () => {
    try {
        if (typeof window === 'undefined' ||
            !window.location ||
            !window.location.href) {
            return false;
        }
        const path = window.location.href.indexOf(IS_PINBOARD_VIZ_EMBED);
        if (path > -1) {
            const params = window.location.href
                .substring(path + IS_PINBOARD_VIZ_EMBED.length + 1)
                .split('/');
            return params.length === 2 && !!params[1].length;
        }
        return false;
    }
    catch (error) {
        return false;
    }
};
const getAdditionalPendoKey = () => {
    const queryParams = getEmbedQueryParams();
    return queryParams[ADDITIONAL_PENDO_KEY] || '';
};
const expandAliasedId = (action) => [
    action,
    ...(ACTION_ALIAS[action] ?? []),
];
const expandAliasedIds = (ids) => {
    const surfaces = ids.flatMap(id => ACTION_ALIAS[id] ?? []);
    return surfaces.length ? [...new Set([...ids, ...surfaces])] : ids;
};
const areActionIdsEquivalent = (actionA, actionB) => {
    if (!actionA || !actionB)
        return false;
    return (expandAliasedId(actionA).includes(actionB) ||
        expandAliasedId(actionB).includes(actionA));
};
const expandActionAliases = (embedParams) => {
    const { hideAction, visibleAction, disableAction, primaryAction, } = embedParams;
    const primaryActionAliases = typeof primaryAction === 'string'
        ? ACTION_ALIAS[primaryAction]
        : undefined;
    return {
        ...embedParams,
        ...(Array.isArray(hideAction) && {
            hideAction: expandAliasedIds(hideAction),
        }),
        ...(Array.isArray(visibleAction) && {
            visibleAction: expandAliasedIds(visibleAction),
        }),
        ...(Array.isArray(disableAction) && {
            disableAction: expandAliasedIds(disableAction),
        }),
        ...(primaryActionAliases && {
            primaryAction: primaryActionAliases[primaryActionAliases.length - 1],
        }),
    };
};
const normalizeIncomingEmbedParams = (embedParams) => {
    const decodedParams = decodeEmbedParams(embedParams);
    const expandedParams = expandActionAliases(decodedParams);
    return expandedParams;
};
const addActionItemMapFromUrlParams = (actionIds) => {
    const modifiedActionIds = actionIds;
    Object.keys(ActionItemMap).forEach(key => {
        if (actionIds.has(key)) {
            ActionItemMap[key].forEach((e) => modifiedActionIds.add(e));
        }
    });
    return modifiedActionIds;
};
const getDisabledActions = (embedConfig) => {
    const { embedParams, isEmbedded } = embedConfig;
    if (!isEmbedded) {
        return new Set();
    }
    const disableActionIds = addActionItemMapFromUrlParams(new Set(embedParams.disableAction));
    return disableActionIds;
};
const getDisabledActionReason = (embedConfig) => {
    const { embedParams } = embedConfig;
    return embedParams?.disableHint;
};
const getHiddenActions = (embedConfig) => {
    const { embedParams, isEmbedded } = embedConfig;
    if (!isEmbedded) {
        return new Set();
    }
    const hiddenActionIds = addActionItemMapFromUrlParams(new Set(embedParams.hideAction));
    return hiddenActionIds;
};
const sendIframeUrlToParent = async () => {
    const iframeUrl = window.location.href;
    return iframeUrl;
};
const getVisibleActions = (embedConfig) => {
    const { embedParams, isEmbedded } = embedConfig;
    if (!isEmbedded) {
        return undefined;
    }
    if (embedParams.visibleAction) {
        const visibleActionsIds = addActionItemMapFromUrlParams(new Set(embedParams.visibleAction));
        return visibleActionsIds;
    }
    return undefined;
};
export const isLazyLoadingForEmbed = (embedConfig) => {
    return !!(embedConfig?.isEmbedded &&
        embedConfig?.embedParams?.isFullHeightPinboard &&
        embedConfig?.embedParams?.isLazyLoadingForEmbedEnabled);
};
export const isFullHeightEnabled = (embedConfig) => {
    const { isEmbedded, embedParams } = embedConfig;
    return (!!isEmbedded &&
        !!embedParams?.isFullHeightPinboard &&
        !embedParams?.disableFullHeightResizeObserver);
};
export const getEmbedLazyLoadingElementId = () => ({
    container: 'lazy-load-embed-container',
});
export const getEmbedRootMarginForLazyLoad = (embedConfig) => {
    return embedConfig?.embedParams?.rootMarginForLazyLoad;
};
const getHookInEmbedMode = (embedHook, noOpMethod = () => ({})) => isEmbeddedApp() ? embedHook : noOpMethod;
const shouldShowAction = (visibleActionIds, hiddenActionIds, actionId, isPinboardAction = false) => {
    if (isLiveboardEmbed() &&
        actionId === Action.MakeACopy &&
        !isPinboardAction)
        return false;
    if (visibleActionIds) {
        return visibleActionIds?.has(actionId);
    }
    if (hiddenActionIds?.has(actionId)) {
        return false;
    }
    return true;
};
const getPinboardVisibleTabsForEmbed = (embedConfig) => {
    const { isEmbedded, embedParams } = embedConfig;
    if (isEmbedded && embedParams.visibleTabs) {
        return new Set(embedParams.visibleTabs);
    }
    return undefined;
};
const setPinboardVisibleTabsForEmbed = (embedConfig, visibleTabIds) => {
    if (!embedConfig?.isEmbedded || !visibleTabIds) {
        return;
    }
    embedConfig.updateEmbedParams((prevParams) => {
        return {
            ...prevParams,
            visibleTabs: visibleTabIds,
            hideTabs: undefined,
        };
    });
};
const getPinboardHiddenTabsForEmbed = (embedConfig) => {
    const { isEmbedded, embedParams } = embedConfig;
    if (isEmbedded && embedParams.hideTabs) {
        return new Set(embedParams.hideTabs);
    }
    return undefined;
};
const setPinboardHiddenTabsForEmbed = (embedConfig, hiddenTabIds) => {
    if (!embedConfig?.isEmbedded || !hiddenTabIds) {
        return;
    }
    embedConfig.updateEmbedParams((prevParams) => {
        return {
            ...prevParams,
            hideTabs: hiddenTabIds,
            visibleTabs: undefined,
        };
    });
};
const shouldHideTabForEmbed = (embedConfig, tabID) => {
    if (!embedConfig?.isEmbedded)
        return false;
    const hiddenTabsForEmbed = getPinboardHiddenTabsForEmbed(embedConfig);
    const visibleTabsForEmbed = getPinboardVisibleTabsForEmbed(embedConfig);
    if (hiddenTabsForEmbed) {
        return hiddenTabsForEmbed?.has(tabID);
    }
    if (visibleTabsForEmbed) {
        return !visibleTabsForEmbed?.has(tabID);
    }
    return false;
};
const getPrimaryAction = (embedConfig) => {
    const { embedParams, isEmbedded } = embedConfig;
    if (!isEmbedded) {
        return false;
    }
    return embedParams?.primaryAction;
};
const isHiddenForEmbed = (embedConfig, actionType, isPinboardAction = false, hiddenActions, visibleActions) => {
    if (isLiveboardEmbed() && actionType === Action.Remove) {
        return true;
    }
    if (isVizEmbed() && actionType === Action.Explore) {
        return true;
    }
    if (embedConfig.isEmbedded && actionType === Action.Annotate) {
        return true;
    }
    const hiddenActionsForEmbed = hiddenActions || getHiddenActions(embedConfig);
    const visibleActionsForEmbed = visibleActions || getVisibleActions(embedConfig);
    return (embedConfig.isEmbedded &&
        !shouldShowAction(visibleActionsForEmbed, hiddenActionsForEmbed, actionType, isPinboardAction));
};
const getEmbedActionStateMap = (embedConfig) => {
    const embedParamsStr = JSON.stringify(embedConfig.embedParams);
    if (!embedActionsMapForParamsMap?.has(embedParamsStr)) {
        const newEmbedActionStateMap = new Map();
        const disabledActions = getDisabledActions(embedConfig);
        const hiddenActions = getHiddenActions(embedConfig);
        const visibleActions = getVisibleActions(embedConfig);
        Object.values(Action).forEach(action => {
            const state = {
                isHidden: isHiddenForEmbed(embedConfig, action, false, hiddenActions, visibleActions),
                isDisabled: disabledActions?.has(action),
            };
            newEmbedActionStateMap.set(action, state);
        });
        embedActionsMapForParamsMap.set(embedParamsStr, newEmbedActionStateMap);
    }
    return embedActionsMapForParamsMap.get(embedParamsStr);
};
const shouldHideForEmbed = (embedConfig, actionType, isPinboardAction = false) => {
    if (isPinboardAction ||
        !Object.values(Action).includes(actionType)) {
        return isHiddenForEmbed(embedConfig, actionType, isPinboardAction);
    }
    const embedParamsStr = JSON.stringify(embedConfig.embedParams);
    if (embedActionsMapForParamsMap
        ?.get(embedParamsStr)
        ?.get(actionType) === undefined) {
        return getEmbedActionStateMap(embedConfig).get(actionType)
            .isHidden;
    }
    return embedActionsMapForParamsMap
        .get(embedParamsStr)
        .get(actionType).isHidden;
};
const shouldDisableForEmbed = (embedConfig, actionType) => {
    if (!Object.values(Action).includes(actionType))
        return false;
    const embedParamsStr = JSON.stringify(embedConfig.embedParams);
    if (embedActionsMapForParamsMap
        ?.get(embedParamsStr)
        ?.get(actionType) === undefined) {
        return getEmbedActionStateMap(embedConfig).get(actionType)
            .isDisabled;
    }
    return embedActionsMapForParamsMap
        .get(embedParamsStr)
        .get(actionType).isDisabled;
};
const getFilterRemovalPermissionFromUrlParam = (embedConfig = {}) => {
    const { embedParams } = embedConfig;
    return !embedParams?.preventPinboardFilterRemoval;
};
const getPinboardVisibleVizsForEmbed = (embedConfig) => {
    const { isEmbedded, embedParams } = embedConfig;
    if (!isEmbedded) {
        return undefined;
    }
    if (embedParams.pinboardVisibleVizs) {
        return new Set(embedParams.pinboardVisibleVizs);
    }
    return undefined;
};
const parseQueryParams = (queryString, paramsObject) => {
    const parsedParams = {};
    const queryParams = new URLSearchParams(queryString);
    queryParams.forEach((value, key) => {
        if (_.isUndefined(parsedParams[key])) {
            parsedParams[key] = [];
        }
        parsedParams[key].push(value);
    });
    Object.keys(parsedParams).forEach(key => {
        const value = parsedParams[key];
        if (value.length === 1) {
            parsedParams[key] = value[0];
        }
    });
    Object.assign(paramsObject, parsedParams);
};
const setRuntimeFilterParams = (data) => {
    parseQueryParams(data, runtimeFilterParams);
};
const setRuntimeParameterParams = (data) => {
    parseQueryParams(data, runtimeParameterParams);
};
const getRuntimeFilterParams = () => {
    return runtimeFilterParams;
};
const getRuntimeParameterParams = () => {
    return runtimeParameterParams;
};
const getVisualOverridesFromEmbedConfig = (embedConfig) => {
    const visualOverrides = embedConfig.embedParams?.visualOverridesParams;
    return !_.isEmpty(visualOverrides) ? visualOverrides : null;
};
const shouldOverrideHistoryStateInEmbed = () => {
    if (!isEmbeddedApp()) {
        return false;
    }
    const queryParams = getEmbedQueryParams();
    return !!queryParams?.overrideHistoryState;
};
const updateParametersWithOverrides = (urlParams, pbParametersOverrides) => {
    const pbParametersOverridesMap = new Map();
    for (let i = 0; i < pbParametersOverrides.length; i++) {
        const pbParametersOverride = pbParametersOverrides[i];
        pbParametersOverridesMap.set(pbParametersOverride.name, pbParametersOverride);
    }
    const urlParamsCopy = { ...urlParams };
    const pbParametersCurrentMap = new Map();
    const urlKeys = Object.keys(urlParams);
    for (let i = 0; i < urlKeys.length; i++) {
        const key = urlKeys[i];
        const runtimeParametersCheck = key.match(/^param(\d+)/);
        if (Object.prototype.hasOwnProperty.call(urlParams, key) &&
            runtimeParametersCheck) {
            const index = runtimeParametersCheck[1];
            const paramName = urlParams[key];
            const paramValue = urlParams[`paramVal${index}`];
            if (pbParametersOverridesMap.has(key)) {
                const overrideParam = pbParametersOverridesMap.get(key);
                urlParamsCopy[key] = overrideParam.value.toString();
                pbParametersCurrentMap.set(paramName, overrideParam);
            }
            else {
                pbParametersCurrentMap.set(paramName, {
                    name: paramName,
                    value: paramValue,
                });
            }
            delete urlParamsCopy[key];
            delete urlParamsCopy[`paramVal${index}`];
        }
    }
    const pbParametersMerged = new Map([
        ...pbParametersCurrentMap,
        ...pbParametersOverridesMap,
    ]);
    let parametersIndex = 1;
    pbParametersMerged.forEach((pbParameterMerged) => {
        urlParamsCopy[`param${parametersIndex}`] = pbParameterMerged.name;
        urlParamsCopy[`paramVal${parametersIndex}`] = pbParameterMerged.value.toString();
        parametersIndex++;
    });
    return urlParamsCopy;
};
const updateRuntimeFiltersWithOverrides = (urlParams, pbFilterOverrides) => {
    const pbFilterOverridesMap = new Map();
    pbFilterOverrides.forEach(f => {
        if (!pbFilterOverridesMap.has(f.columnName)) {
            pbFilterOverridesMap.set(f.columnName, []);
        }
        pbFilterOverridesMap.get(f.columnName).push(f);
    });
    const pbFilterCurrentMap = new Map();
    const urlParamsCopy = { ...urlParams };
    Object.keys(urlParams).forEach((key) => {
        const runtimeFilterCheck = key.match(/^col(\d+)/);
        if (!runtimeFilterCheck) {
            return;
        }
        const idx = runtimeFilterCheck[1];
        if (!pbFilterCurrentMap.has(urlParams[key])) {
            pbFilterCurrentMap.set(urlParams[key], []);
        }
        pbFilterCurrentMap.get(urlParams[key]).push({
            columnName: urlParams[key],
            operator: urlParams[`op${idx}`],
            values: urlParams[`val${idx}`],
        });
        delete urlParamsCopy[key];
        delete urlParamsCopy[`op${idx}`];
        delete urlParamsCopy[`val${idx}`];
    });
    const pbFilterMerged = new Map([
        ...pbFilterCurrentMap,
        ...pbFilterOverridesMap,
    ]);
    let filterIdx = 1;
    pbFilterMerged.forEach(filters => {
        filters.forEach((f) => {
            urlParamsCopy[`col${filterIdx}`] = f.columnName;
            urlParamsCopy[`op${filterIdx}`] = f.operator;
            urlParamsCopy[`val${filterIdx}`] =
                f.values.length === 1 ? f.values[0] : f.values;
            filterIdx++;
        });
    });
    return urlParamsCopy;
};
const useEmbedEventsRootHandler = ({ useEmbedEventSubscription, updateEmbedParams, updateRuntimeFilterParams, runtimeFilterParams, updateEmbedParameterParams, embedParameterParams, setParameterOverridesSubject, dispatchValidationError, }, hostevent2Config) => {
    useEmbedEventSubscription(IncomingEventType.UpdateParameters, (updatedParams, context) => {
        const currentContext = getNavigationContext();
        if (!currentContext.stack.filter((ctx) => ctx.name === context)
            .length &&
            hostevent2Config.useHostEventsV2) {
            dispatchValidationError?.('This event is not allowed in this context.');
            return;
        }
        if (!updatedParams?.length)
            return;
        const visibleUpdatedParams = updatedParams.filter(param => param.isVisibleToUser);
        const hiddenUpdatedParams = updatedParams.filter(param => !param.isVisibleToUser);
        if (visibleUpdatedParams.length) {
            setParameterOverridesSubject.next(visibleUpdatedParams);
        }
        if (hiddenUpdatedParams.length) {
            if (!_.isEmpty(embedParameterParams)) {
                updateEmbedParameterParams((prevParams) => {
                    return updateParametersWithOverrides(prevParams, hiddenUpdatedParams);
                });
            }
            else {
                updateEmbedParams((prevParams) => {
                    return updateParametersWithOverrides(prevParams, hiddenUpdatedParams);
                });
            }
        }
    });
    useEmbedEventSubscription(IncomingEventType.UpdateRuntimeFilters, (updatedFilters, context) => {
        const currentContext = getNavigationContext();
        if (!currentContext.stack.filter((ctx) => ctx.name === context)
            .length &&
            hostevent2Config.useHostEventsV2) {
            dispatchValidationError?.('This event is not allowed in this context.');
            return;
        }
        if (!!updatedFilters && !!updatedFilters.length) {
            if (!_.isEmpty(runtimeFilterParams)) {
                updateRuntimeFilterParams((prevParams) => {
                    return updateRuntimeFiltersWithOverrides(prevParams, updatedFilters);
                });
            }
            else {
                updateEmbedParams((prevParams) => {
                    return updateRuntimeFiltersWithOverrides(prevParams, updatedFilters);
                });
            }
        }
    });
    useEmbedEventSubscription(IncomingEventType.Navigate, (data) => {
        let path = data;
        let replace = false;
        if (typeof data === 'object') {
            path = data?.path;
            replace = !!data?.replace;
        }
        if (typeof path === 'number') {
            window.history.go(path);
            return;
        }
        if (replace || shouldOverrideHistoryStateInEmbed()) {
            window.location.replace(`#/${path}`);
        }
        else if (isSafariBrowser() && isSafariOptimizationEnabled()) {
            const navUrl = new URL(window.location.href);
            navUrl.searchParams.set('t', `${Date.now()}`);
            navUrl.hash = `#/${path}`;
            window.location.href = navUrl.toString();
        }
        else {
            window.location.hash = `#/${path}`;
        }
    });
    useEmbedEventSubscription(IncomingEventType.GetIframeUrl, sendIframeUrlToParent);
    useEmbedEventSubscription(IncomingEventType.GetPageContext, async () => {
        const pageContext = await getNavigationContext();
        return pageContext;
    });
};
const isSafariBrowser = () => {
    if (typeof navigator === 'undefined') {
        return false;
    }
    const userAgent = navigator.userAgent.toLowerCase();
    const hasSafariInUA = userAgent.includes('safari');
    const isNonSafariBrowser = userAgent.includes('chrome') ||
        userAgent.includes('chromium') ||
        userAgent.includes('edg/') ||
        userAgent.includes('opr/') ||
        userAgent.includes('brave') ||
        userAgent.includes('crios') ||
        userAgent.includes('edgios') ||
        userAgent.includes('fxios') ||
        userAgent.includes('opios');
    return hasSafariInUA && !isNonSafariBrowser;
};
const isRuntimeFilterParam = (val, key) => key.match(/^(col|op|val)(\d+)/);
const isRuntimeParametersParam = (val, key) => key.match(/^(param|paramVal)(\d+)/);
const useEmbedParamsChanged = (isRuntimeParam, callback) => {
    const { embedParams, runtimeFilterParams, embedParameterParams, dispatchEmbedEvent, } = useEmbedContext();
    const prevParams = React.useRef(null);
    React.useEffect(() => {
        if (prevParams.current) {
            const prevEmbedParams = _.pickBy(prevParams.current, isRuntimeParam
                ? isRuntimeParametersParam
                : isRuntimeFilterParam);
            const currentEmbedParams = _.pickBy(embedParams, isRuntimeParam
                ? isRuntimeParametersParam
                : isRuntimeFilterParam);
            if (!_.isEqual(prevEmbedParams, currentEmbedParams)) {
                callback();
            }
        }
        prevParams.current = isRuntimeParam
            ? embedParameterParams || embedParams
            : runtimeFilterParams || embedParams;
    }, [embedParams, runtimeFilterParams, embedParameterParams]);
};
const useEmbedRuntimeFiltersChanged = (callback) => {
    useEmbedParamsChanged(false, callback);
};
const useEmbedRuntimeParametersChanged = (callback) => {
    useEmbedParamsChanged(true, callback);
};
const useLayoutChangeNotifier = (dispatchEmbedEvent) => {
    const currentHeight = React.useRef(null);
    return {
        updateHeight: (height) => {
            const height_diff = Math.abs(currentHeight.current - height);
            if (height_diff > MIN_DIFF) {
                currentHeight.current = height + BUFFER_HEIGHT;
                dispatchEmbedEvent(EventType.EMBED_HEIGHT, currentHeight.current);
            }
        },
    };
};
const getVizGUID = (answerId) => {
    return answerIdToVizGUIDMap.get(answerId);
};
const setAnswerIdToVizGUIDMap = (answerId, vizGUID) => {
    answerIdToVizGUIDMap.set(answerId, vizGUID);
};
const propertyTemplate = (property, value) => `${property}: ${value};`;
const ruleTemplate = (ruleName, styles) => `${ruleName} {
    ${styles}
}`;
const convertCustomCSSObjectToStyle = (customCSS) => {
    const { variables = {}, rules_UNSTABLE: rulesUnstable = {} } = customCSS;
    const variableStyles = _.keys(variables).map(variable => propertyTemplate(variable, variables[variable]));
    const variableCss = ruleTemplate(':root', variableStyles.join('\n'));
    const rules = _.keys(rulesUnstable)
        .map(ruleName => ruleTemplate(ruleName, _.keys(rulesUnstable[ruleName])
        .map(field => propertyTemplate(field, rulesUnstable[ruleName][field]))
        .join('\n')))
        .join('\n');
    return `
        ${variableCss}
        ${rules}
    `;
};
const setEmbedCssStyles = (_embedCssStyles) => {
    embedCssStyles = _embedCssStyles;
};
const getEmbedCssStyles = () => {
    if (_.isEmpty(appThemeCssVariables)) {
        return embedCssStyles;
    }
    const customerCustomCss = (embedCssStyles?.customCSS ??
        {});
    return {
        ...embedCssStyles,
        customCSS: {
            ...customerCustomCss,
            variables: {
                ...appThemeCssVariables,
                ...(customerCustomCss.variables ?? {}),
            },
        },
    };
};
const getEmbedCustomCssUrl = () => {
    const url = embedCssStyles?.customCSSUrl;
    return _.isString(url) && url ? url : undefined;
};
const comesAfter = (reference, node) => !!(reference.compareDocumentPosition(node) &
    Node.DOCUMENT_POSITION_FOLLOWING);
const getFirstCustomerCssElement = () => {
    const elements = [CUSTOM_CSS_URL_ELEMENT_ID, CUSTOM_CSS_ELEMENT_ID]
        .map(id => document.getElementById(id))
        .filter((el) => !!el);
    return elements.reduce((first, el) => (!first || comesAfter(el, first) ? el : first), null);
};
const dropInlineAppThemeVarPins = () => {
    const root = document.documentElement;
    _.keys(appThemeCssVariables)
        .filter(name => name.startsWith(EXTERNAL_VARIABLE_PREFIX))
        .forEach(name => root.style.removeProperty(name));
};
const addCustomCssElements = (data) => {
    const customizations = data?.customizations || data?.customisations;
    if (customizations?.style) {
        setEmbedCssStyles(customizations?.style);
    }
    if (customizations?.style?.customCSSUrl) {
        let link;
        if (!document.getElementById(CUSTOM_CSS_URL_ELEMENT_ID)) {
            link = document.createElement('link');
            link.setAttribute('id', CUSTOM_CSS_URL_ELEMENT_ID);
            link.setAttribute('rel', 'stylesheet');
            link.setAttribute('type', 'text/css');
            document.body.appendChild(link);
        }
        else {
            link = document.getElementById(CUSTOM_CSS_URL_ELEMENT_ID);
        }
        link.href = customizations?.style?.customCSSUrl;
        dropInlineAppThemeVarPins();
    }
    if (customizations?.style?.customCSS) {
        setEmbedCssParams(customizations.style.customCSS?.variables);
        if (document.getElementById(CUSTOM_CSS_ELEMENT_ID)) {
            document.getElementById(CUSTOM_CSS_ELEMENT_ID).remove();
        }
        const customCssEl = document.createElement('style');
        customCssEl.setAttribute('id', CUSTOM_CSS_ELEMENT_ID);
        customCssEl.setAttribute('type', 'text/css');
        customCssEl.setAttribute('nonce', getNonce());
        customCssEl.innerHTML = convertCustomCSSObjectToStyle(customizations?.style?.customCSS);
        document.body.appendChild(customCssEl);
        dropInlineAppThemeVarPins();
    }
    return null;
};
const setAppThemeCssVariables = (variables = {}) => {
    appThemeCssVariables = variables ?? {};
    const names = _.keys(appThemeCssVariables);
    const existing = document.getElementById(APP_THEME_CSS_ELEMENT_ID);
    if (!names.length) {
        existing?.remove();
        return;
    }
    const themeCssEl = existing ?? document.createElement('style');
    if (!existing) {
        themeCssEl.setAttribute('id', APP_THEME_CSS_ELEMENT_ID);
        themeCssEl.setAttribute('type', 'text/css');
        themeCssEl.setAttribute('nonce', getNonce());
    }
    themeCssEl.innerHTML = ruleTemplate(':root', names
        .map(name => propertyTemplate(name, appThemeCssVariables[name]))
        .join('\n'));
    const firstCustomerCssEl = getFirstCustomerCssElement();
    if (!themeCssEl.isConnected) {
        if (firstCustomerCssEl) {
            firstCustomerCssEl.before(themeCssEl);
        }
        else {
            document.body.appendChild(themeCssEl);
        }
        return;
    }
    if (firstCustomerCssEl && comesAfter(firstCustomerCssEl, themeCssEl)) {
        firstCustomerCssEl.before(themeCssEl);
    }
};
const setEmbedQueryParamsFromInitData = (data) => {
    const embedParamsObject = data?.embedParams;
    if (embedParamsObject) {
        embedQueryPramsFromInitData = embedParamsObject;
    }
    else {
        embedQueryPramsFromInitData = _.omitBy({
            searchTokenString: data?.searchOptions?.searchTokenString,
        }, _.isUndefined);
    }
};
const getEmbedQueryParamsFromInitData = () => {
    return embedQueryPramsFromInitData;
};
const isNewBreakpointEnabledInTSE = (embedConfig) => {
    const { embedParams } = embedConfig;
    return !!embedParams?.enable2ColumnLayout;
};
const isLiveboardAlwaysOn12ColLayoutInTSE = (embedConfig) => {
    const { embedParams } = embedConfig;
    return (embedParams?.isLiveboardAlwaysOn12ColLayout === true ||
        embedParams?.isLiveboardAlwaysOn12ColLayout === 'true');
};
export const isLiveboardStylingAndGroupingEnabledInEmbed = (embedConfig) => {
    return (embedConfig?.embedParams?.isLiveboardStylingAndGroupingEnabled ||
        embedConfig?.embedParams?.isLiveboardMasterpiecesEnabled);
};
export const getLiveboardGutterForEmbed = (embedConfig) => {
    if (!embedConfig?.isEmbedded) {
        return undefined;
    }
    const parsed = Number.parseInt(embedConfig?.embedParams?.liveboardGutter, 10);
    return Number.isNaN(parsed) || parsed < 0 ? undefined : parsed;
};
const isStopAnswerGenerationEnabledInEmbed = (embedConfig) => {
    return !!embedConfig?.embedParams?.enableStopAnswerGenerationEmbed;
};
export const isMuzeChartPhase1EnabledGAInEmbed = (embedConfig) => {
    return embedConfig?.embedParams?.muzeChartPhase1EnabledGA || false;
};
const isBreakpointValid = (value) => {
    return !(Number.isNaN(value) || value < BREAKPOINT_LOWER_BOUND);
};
const getVariableValue = (variable, defaultValue) => {
    const value = Math.floor(Number(getEmbedCssParams(variable)));
    return isBreakpointValid(value) ? value : defaultValue;
};
const getEmbedResponsiveBreakpoints = (isLiveboardNewBreakPointsEnabled) => {
    if (!isLiveboardNewBreakPointsEnabled) {
        return {
            firstBreakpoint: FIRST_BREAKPOINT_DEFAULT_VALUE,
        };
    }
    const firstBreakpoint = getVariableValue(FIRST_BREAKPOINT_BREAKPOINT_VARIABLE, FIRST_BREAKPOINT_DEFAULT_VALUE);
    const secondBreakpoint = getVariableValue(SECOND_BREAKPOINT_BREAKPOINT_VARIABLE, SECOND_BREAKPOINT_DEFAULT_VALUE);
    if (firstBreakpoint < secondBreakpoint) {
        return {
            firstBreakpoint: FIRST_BREAKPOINT_DEFAULT_VALUE,
            secondBreakpoint: SECOND_BREAKPOINT_DEFAULT_VALUE,
        };
    }
    return {
        firstBreakpoint,
        secondBreakpoint,
    };
};
const updateAnswerRuntimeParams = (parameters, currentEmbedParams, cb) => {
    const dataTypes = [];
    const parameterIds = [];
    const values = [];
    for (let i = 1; i <= Object.keys(currentEmbedParams).length / 2; i++) {
        const paramKey = `param${i}`;
        const paramValKey = `paramVal${i}`;
        if (currentEmbedParams[paramKey]) {
            const paramName = currentEmbedParams[paramKey];
            const paramVal = currentEmbedParams[paramValKey];
            const filteredParam = parameters.find((param) => param.name === paramName);
            if (filteredParam) {
                dataTypes.push(filteredParam.dataType);
                parameterIds.push(filteredParam.id);
                values.push(paramVal);
            }
        }
    }
    cb(parameterIds, values, dataTypes);
};
const sdkVersion = () => {
    const queryParams = getEmbedQueryParams();
    return queryParams['sdkVersion'];
};
const parentOrigin = () => {
    const queryParams = getEmbedQueryParams();
    return queryParams['hostAppUrl'];
};
const parentProtocol = () => {
    const queryParams = getEmbedQueryParams();
    return queryParams.hostAppProtocol;
};
let interceptUrls = [];
const setInterceptUrls = (urls) => {
    interceptUrls = urls;
};
const getInterceptUrls = () => {
    return interceptUrls;
};
const DEFAULT_INTERCEPT_TIMEOUT = 30 * 1000;
let interceptTimeout = DEFAULT_INTERCEPT_TIMEOUT;
const setInterceptTimeout = (timeout) => {
    interceptTimeout = timeout ?? DEFAULT_INTERCEPT_TIMEOUT;
};
const getInterceptTimeout = () => {
    return interceptTimeout;
};
const shouldBypassValidation = (config) => {
    if (config?.shouldBypassPayloadValidation != null) {
        return config.shouldBypassPayloadValidation;
    }
    return !config?.useHostEventsV2;
};
export { setHostConfig, getHostConfig, setHiddenHomeLeftNavItems, getHiddenHomeLeftNavItems, setEmbedCssParams, getEmbedCssParams, setHiddenHomepageModules, getHiddenHomepageModules, setReorderedHomepageModules, getReorderedHomepageModules, JsonStringtoArrayRequestHeaders, urlAuthHeadersForCutomActions, isFullAppEmbed, isLBHeaderHiddenInEmbed, isLiveboardEmbed, isOnBeforeGetVizDataInterceptEnabled, isProductTour, isSageEmbed, isSearchBarHidden, isSearchEmbed, hideEurekaSuggestions, disableWorksheetChange, hideWorksheetSelector, hideSearchBarTitle, hideSageAnswerHeader, isFilterPanelHiddenInEmbed, shouldShowLBTitleInEmbed, shouldShowLBDescriptionInEmbed, shouldShowLiveboardVerifiedBadge, shouldShowLiveboardReverifyBanner, linkOverrideEnabled, isLinkOverridesV2Enabled, isDisableRedirectionLinksInNewTabEnabled, isVizEmbed, getAdditionalPendoKey, getVisibleActions, sendIframeUrlToParent, getHiddenActions, getDisabledActionReason, getDisabledActions, addActionItemMapFromUrlParams, expandActionAliases, areActionIdsEquivalent, expandAliasedId, normalizeIncomingEmbedParams, getHookInEmbedMode, shouldShowAction, getPinboardVisibleTabsForEmbed, getPinboardHiddenTabsForEmbed, setPinboardHiddenTabsForEmbed, setPinboardVisibleTabsForEmbed, shouldHideTabForEmbed, isHiddenForEmbed, getEmbedActionStateMap, shouldHideForEmbed, shouldDisableForEmbed, getFilterRemovalPermissionFromUrlParam, getPinboardVisibleVizsForEmbed, setRuntimeFilterParams, setRuntimeParameterParams, getRuntimeFilterParams, getRuntimeParameterParams, getVisualOverridesFromEmbedConfig, updateParametersWithOverrides, updateRuntimeFiltersWithOverrides, useEmbedEventsRootHandler, useEmbedParamsChanged, useEmbedRuntimeFiltersChanged, useEmbedRuntimeParametersChanged, useLayoutChangeNotifier, getVizGUID, setAnswerIdToVizGUIDMap, convertCustomCSSObjectToStyle, addCustomCssElements, setAppThemeCssVariables, getEmbedQueryParamsFromInitData, isNewBreakpointEnabledInTSE, isLiveboardAlwaysOn12ColLayoutInTSE, getEmbedResponsiveBreakpoints, updateAnswerRuntimeParams, setEmbedQueryParamsFromInitData, isRuntimeFilterParam, isRuntimeParametersParam, getEmbedCssStyles, getEmbedCustomCssUrl, setEmbedCssStyles, setHiddenListColumns, getHiddenListColumns, parseQueryParams, sdkVersion, parentOrigin, parentProtocol, isMobileWebViewMessagingEnabled, isMobileEmbedShell, getPrimaryAction, isSpotterAgentEmbed, isSpotterEmbed, setCustomActions, getCustomActions, setInterceptUrls, getInterceptUrls, isApiInterceptDisabled, setInterceptTimeout, getInterceptTimeout, shouldShowMaskedFilterChipInEmbed, shouldBypassValidation, isStopAnswerGenerationEnabledInEmbed, shouldOverrideHistoryStateInEmbed, shouldShowAnswerEditPanelInEmbed, };
//# sourceMappingURL=embed-util.js.map